/**
 * 把一段完整 HTML 渲染进离屏 iframe，等图片加载完后交出 body。
 * 打印截图/PDF 导出/签署文档图都用它拿「output 模式」的干净 DOM——
 * 预览 iframe 里可能带编辑态的占位框/描边，不能直接当成品截。
 */
/** 1mm 在 96dpi 下的像素数。离屏宽度必须等于纸张宽度，否则导出的字号和边距都不对。 */
const PX_PER_MM = 96 / 25.4;

export function renderHtmlOffscreen(
  html: string,
  /** 纸张宽度（mm）。给了就按它设离屏宽度；不给沿用旧的 900px。 */
  paperWidthMm?: number,
): Promise<{ body: HTMLElement; cleanup: () => void }> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    const widthPx = paperWidthMm ? Math.round(paperWidthMm * PX_PER_MM) : 900;
    iframe.style.cssText =
      `position:fixed;left:-10000px;top:0;width:${widthPx}px;height:1200px;border:0;visibility:hidden;`;
    const cleanup = () => {
      try {
        iframe.remove();
      } catch {
        /* noop */
      }
    };
    iframe.addEventListener(
      'load',
      () => {
        const body = iframe.contentDocument?.body;
        if (!body) {
          cleanup();
          reject(new Error('离屏渲染失败'));
          return;
        }
        const imgs = Array.from(body.querySelectorAll('img'));
        Promise.all(
          imgs.map((img) =>
            img.complete
              ? Promise.resolve()
              : new Promise<void>((r) => {
                  // 必须用 addEventListener：直接赋值 onerror 会顶掉渲染器挂在
                  // 图片上的「代理失败退回直连」回退，于是预览里好好的图，
                  // 导出 PDF / 签署文档时变成一个个破图。
                  img.addEventListener('load', () => r(), { once: true });
                  img.addEventListener('error', () => setTimeout(r, 400), { once: true });
                }),
          ),
        ).then(() => setTimeout(() => resolve({ body, cleanup }), 60));
      },
      { once: true },
    );
    iframe.srcdoc = html;
    document.body.appendChild(iframe);
  });
}
